import { ensureCsrfToken, getAuthenticatedRequestHeaders } from "../Auth/serverSessionApi.ts";
import type { FeedbackItem, FeedbackStatus, PublicFeedbackItem } from "../Domain/Feedback.ts";

export interface FeedbackUploadInput {
	id: string;
	name: string;
	kind: "image" | "video" | "screenshot";
	dataUrl: string;
}

export interface CreateFeedbackInput {
	title: string;
	contentHtml: string;
	contentText: string;
	context: {
		url: string;
		path: string;
		viewportWidth: number;
		viewportHeight: number;
		userAgent: string;
		matchId?: string;
	};
	uploads: FeedbackUploadInput[];
}

export class FeedbackApiError extends Error {
	public constructor(
		public readonly code: string,
		public readonly status: number,
	) {
		super(code);
		this.name = "FeedbackApiError";
	}
}

const readError = async (response: Response): Promise<string> => {
	try {
		const payload = (await response.json()) as { error?: unknown };
		if (typeof payload.error === "string") return payload.error;
	} catch {
		// Keep the status fallback below.
	}
	return `Request failed (${response.status})`;
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
	if (init?.method && init.method !== "GET") await ensureCsrfToken();
	const headers = new Headers(init?.headers);
	if (init?.body) headers.set("Content-Type", "application/json");
	for (const [name, value] of Object.entries(getAuthenticatedRequestHeaders())) {
		headers.set(name, value);
	}
	const response = await fetch(path, {
		...init,
		headers,
	});
	if (!response.ok) throw new FeedbackApiError(await readError(response), response.status);
	return (await response.json()) as T;
};

export const listFeedback = async (): Promise<FeedbackItem[]> =>
	(await request<{ feedback: FeedbackItem[] }>("/api/feedback")).feedback;

export const getFeedback = async (id: string): Promise<FeedbackItem> =>
	(await request<{ feedback: FeedbackItem }>(`/api/feedback/${encodeURIComponent(id)}`)).feedback;

export const createFeedback = async (input: CreateFeedbackInput): Promise<PublicFeedbackItem> =>
	(
		await request<{ feedback: PublicFeedbackItem }>("/api/feedback", {
			method: "POST",
			body: JSON.stringify(input),
		})
	).feedback;

export const addFeedbackComment = async (id: string, body: string): Promise<FeedbackItem> =>
	(
		await request<{ feedback: FeedbackItem }>(`/api/feedback/${encodeURIComponent(id)}/comments`, {
			method: "POST",
			body: JSON.stringify({ body }),
		})
	).feedback;

export const deleteFeedbackAttachment = async (
	id: string,
	attachmentId: string,
): Promise<FeedbackItem> =>
	(
		await request<{ feedback: FeedbackItem }>(
			`/api/feedback/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
			{ method: "DELETE" },
		)
	).feedback;

export const updateFeedbackWorkflow = async (
	id: string,
	input: {
		status?: FeedbackStatus;
		aiRequested?: boolean;
		aiInstructions?: string;
		adminPlan?: string;
		executePlanDirectly?: boolean;
		deployDirectly?: boolean;
		uploads?: FeedbackUploadInput[];
	},
): Promise<FeedbackItem> =>
	(
		await request<{ feedback: FeedbackItem }>(`/api/feedback/${encodeURIComponent(id)}/workflow`, {
			method: "PATCH",
			body: JSON.stringify(input),
		})
	).feedback;
